import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getOrganisations } from '../../actions';
import { FetchError, IOrganisation } from '../../models';
import { RootState } from '../rootReducer';
import { getBootstrapAction } from './BootstrapSlice';

export interface IOrganisationState {
    organisation: IOrganisation;
    organisations: IOrganisation[];
}

export const initialState: IOrganisationState = {
    organisation: { id: '' },
    organisations: [],
};

const name = 'organisation';

const organisationSlice = createSlice({
    initialState,
    name,
    reducers: {
        setTenantId(state, action: PayloadAction<string>) {
            state.organisation.id = action.payload;
        },
        setOrganisation(state, action: PayloadAction<IOrganisation>) {
            state.organisation = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getBootstrapAction.fulfilled, (state, { payload }) => {
                state.organisation.id = payload.tenantId;
                state.organisation.baseCurrency = payload.organisationBaseCurrency;
                state.organisation.class = payload.organisationClass;
                state.organisation.region = payload.organisationRegion;
                state.organisation.timezone = payload.organisationTimezone;
                state.organisation.paysTax = payload.organisationPaysTax;
            })
            .addCase(getOrganisationsAction.fulfilled, (state, { payload }) => {
                state.organisations = payload ?? [];
            });
    },
});

export const { setTenantId, setOrganisation } = organisationSlice.actions;

export const getOrganisationsAction = createAsyncThunk<
    IOrganisation[],
    void,
    {
        state: RootState;
        rejectValue: FetchError;
    }
>(`${name}/getOrganisations`, async (_, { getState, rejectWithValue }) => {
    try {
        const appState = getState();
        const user = appState.user.user;
        const integrationType = appState.installation.installation.integrationType;
        return await getOrganisations(user, integrationType);
    } catch (error) {
        if (error instanceof FetchError) {
            // api response errors i.e. 400 and 404
            return rejectWithValue({ name: error.name, message: error.message, code: error.code, body: error.body });
        }
        return rejectWithValue(error);
    }
});

export const organisationSelectors = {
    organisations: (state: RootState) => state.organisation.organisations,
    organisation: (state: RootState) => state.organisation.organisation,
};

export default organisationSlice.reducer;
